import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft } from "lucide-react";

import apiClient from "../../../api-client";
import { Button } from "../../../components/Button";
import { useAuth } from "../../../components/contexts/AuthContext";
import { getData } from "../../../components/hooks/getData";
import { ContentConfirmationModal } from "../Modals/ContentConfirmationModal";
import { UnderlineTabs } from "../../Clients/metrics/UnderlineTabs";
import { PieceDetail, contentListPath, pageFrameSx, useToast } from "../piece/ContentPiecePage";
import { GroupApprovalBoard } from "../workflow/GroupApprovalBoard";

const SUMMARY_TAB = "resumen";

/**
 * A content group (batch) as one page: a summary tab with every piece side by side,
 * approved as a block, and one tab per piece with its full detail (?pieza=<id>).
 */
export function ContentGroupPage({ isClientView = false }) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageContext } = useOutletContext();
  const { interceptorReady } = useAuth();
  const { notify, element: toast } = useToast();
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: group, isLoading, error } = getData(
    `content-generation/groups/${groupId}/`,
    interceptorReady && Boolean(groupId),
    [groupId, refreshKey]
  );

  const pieces = useMemo(() => group?.pieces || [], [group]);
  const goBack = useCallback(
    () => navigate(contentListPath(isClientView)),
    [navigate, isClientView]
  );

  const tabs = useMemo(
    () => [
      { value: SUMMARY_TAB, label: "Resumen" },
      ...pieces.map((piece) => ({
        value: String(piece.id),
        label: piece.title || `Contenido #${piece.id}`,
      })),
    ],
    [pieces]
  );
  const pieceParam = searchParams.get("pieza");
  const activeTab = tabs.some((tab) => tab.value === pieceParam) ? pieceParam : SUMMARY_TAB;
  const activeTabLabel = tabs.find((tab) => tab.value === activeTab)?.label;

  const selectTab = useCallback(
    (value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === SUMMARY_TAB) next.delete("pieza");
        else next.set("pieza", value);
        return next;
      });
    },
    [setSearchParams]
  );
  const showSummary = useCallback(() => selectTab(SUMMARY_TAB), [selectTab]);

  useEffect(() => {
    const groupName = group?.name || "Tanda de contenidos";
    const isPieceTab = activeTab !== SUMMARY_TAB;
    // Same "Tipo: nombre" title as the other detail pages; the arrow goes back one level.
    setPageContext({
      title: group ? (
        <>
          Grupo de contenidos:{"  "}
          <span style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}>{groupName}</span>
        </>
      ) : "",
      showTitle: Boolean(group),
      leftContent: (
        <IconButton
          onClick={isPieceTab ? showSummary : goBack}
          aria-label={isPieceTab ? "Volver a la tanda" : "Volver a contenidos"}
          size="small"
          sx={{ color: "primary.main", padding: "4px", "&:hover": { backgroundColor: "action.hover" } }}
        >
          <ChevronLeft size={20} />
        </IconButton>
      ),
      titleRightContent: null,
      rightContent: null,
      breadcrumbItems: [
        { label: "Contenidos", onClick: goBack },
        isPieceTab ? { label: groupName, onClick: showSummary } : { label: groupName },
        ...(isPieceTab ? [{ label: activeTabLabel }] : []),
      ],
    });
  }, [setPageContext, goBack, showSummary, group, activeTab, activeTabLabel, theme.palette.text.secondary]);

  const handleConfirmApprove = async () => {
    setShowApproveModal(false);
    setIsAccepting(true);
    try {
      await apiClient.post(`content-generation/groups/${groupId}/approve/`);
      notify("Tanda aprobada correctamente");
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      notify(err?.response?.data?.error || "Error al aprobar la tanda", true);
    } finally {
      setIsAccepting(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ ...pageFrameSx, alignItems: "center", justifyContent: "center", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          No se pudo cargar esta tanda de contenidos.
        </Typography>
        <Button variant="outline" onClick={goBack}>
          Volver a contenidos
        </Button>
      </Box>
    );
  }

  if (isLoading || !group) {
    return (
      <Box sx={{ ...pageFrameSx, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSummary = activeTab === SUMMARY_TAB;

  return (
    <Box
      sx={
        isSummary
          ? { ...pageFrameSx, overflow: "auto", gap: 2 }
          : { display: "flex", flexDirection: "column", gap: 2 }
      }
    >
      {toast}
      <UnderlineTabs tabs={tabs} value={activeTab} onChange={selectTab} />

      {isSummary ? (
        <GroupApprovalBoard
          group={group}
          pieces={pieces}
          isAccepted={group.status === "client_approved"}
          isAccepting={isAccepting}
          onAcceptGroup={() => setShowApproveModal(true)}
          onOpenPiece={(pieceId) => selectTab(String(pieceId))}
          onOpenImage={setPreviewImageUrl}
          showStepper={!isClientView}
        />
      ) : (
        <PieceDetail
          key={activeTab}
          pieceId={activeTab}
          isClientView={isClientView}
          notify={notify}
          onClose={showSummary}
          closeLabel="Volver a la tanda"
          frameSx={{ display: "flex", flexDirection: "column" }}
        />
      )}

      <ContentConfirmationModal
        open={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleConfirmApprove}
        action="approve"
        contentData={{ type: "group", name: group.name, title: group.name }}
        isLoading={isAccepting}
      />

      {previewImageUrl && (
        <Box
          onClick={() => setPreviewImageUrl("")}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            backgroundColor: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            p: 2,
          }}
        >
          <Box
            component="img"
            src={previewImageUrl}
            alt=""
            sx={{ maxWidth: "100%", maxHeight: "100%", borderRadius: "8px" }}
          />
        </Box>
      )}
    </Box>
  );
}
